import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import '../styles/pulse-command-premium.css';

function isMacPlatform() {
  if (typeof navigator === 'undefined') return false;
  return /mac/i.test(navigator.platform || navigator.userAgent || '');
}

/** Dock lateral (peek + clic), mismo patrón que WhatsApp y atajos */
export function PulseCommandDock({ isOpen, onOpen }) {
  if (typeof document === 'undefined' || isOpen) return null;

  const modLabel = isMacPlatform() ? '⌘' : 'Ctrl';

  return createPortal(
    <div className="pulse-cmd-dock" aria-live="polite">
      <div className="pulse-cmd-dock__rail">
        <button
          type="button"
          className="pulse-cmd-dock__btn"
          onClick={onOpen}
          aria-label="Abrir Pulse Command. Búsqueda global del CRM."
          title={`Pulse Command (${modLabel}+K)`}
        >
          <span className="pulse-cmd-dock__pulse" aria-hidden="true" />
          <span className="pulse-cmd-dock__icon" aria-hidden="true">
            ◈
          </span>
          <span className="pulse-cmd-dock__key" aria-hidden="true">
            K
          </span>
        </button>
      </div>
    </div>,
    document.body
  );
}

export default function PulseCommandPalette({
  isOpen,
  query,
  onQueryChange,
  groups,
  flatItems,
  activeIndex,
  onActiveIndexChange,
  loading,
  onClose,
  onRun,
  onKeyDown
}) {
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const modLabel = isMacPlatform() ? '⌘' : 'Ctrl';

  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 40);
    return () => clearTimeout(t);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !listRef.current) return;
    const active = listRef.current.querySelector('.pulse-cmd__item.is-active');
    if (!active) return;
    active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeIndex, isOpen, flatItems.length, groups]);

  if (!isOpen || typeof document === 'undefined') return null;

  let rowOffset = 0;

  return createPortal(
    <div className="pulse-cmd-overlay is-open" role="presentation" onClick={onClose}>
      <div
        className="pulse-cmd"
        role="dialog"
        aria-modal="true"
        aria-label="Pulse Command"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          onKeyDown?.(e);
        }}
      >
        <div className="pulse-cmd__accent" aria-hidden="true" />

        <div className="pulse-cmd__search-row">
          <span className="pulse-cmd__search-icon" aria-hidden="true">
            ◈
          </span>
          <input
            ref={inputRef}
            type="text"
            className="pulse-cmd__input"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Buscar paciente, conversación, pedido, módulo…"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            aria-label="Pulse Command"
          />
          <kbd className="pulse-cmd__kbd">{modLabel}K</kbd>
        </div>

        <div className="pulse-cmd__body" ref={listRef}>
          {loading && flatItems.length === 0 ? (
            <div className="pulse-cmd__state" role="status">
              <span className="crm-gd-spinner" aria-hidden="true" />
              <span>Buscando en Pulse…</span>
            </div>
          ) : null}

          {!loading && flatItems.length === 0 ? (
            <div className="pulse-cmd__state">
              <span>Sin resultados. Probá otro término o una acción rápida.</span>
            </div>
          ) : null}

          {groups.map((section) => (
            <section key={section.key} className="pulse-cmd__section">
              <h3 className="pulse-cmd__section-title">{section.label}</h3>
              <ul className="pulse-cmd__list">
                {section.items.map((item) => {
                  const index = rowOffset;
                  rowOffset += 1;
                  const isActive = index === activeIndex;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={`pulse-cmd__item${isActive ? ' is-active' : ''}`}
                        data-active={isActive ? 'true' : undefined}
                        onMouseEnter={() => onActiveIndexChange(index)}
                        onClick={() => onRun(item)}
                      >
                        <span className="pulse-cmd__item-icon" aria-hidden="true">
                          {item.icon}
                        </span>
                        <span className="pulse-cmd__item-copy">
                          <span className="pulse-cmd__item-title">{item.title}</span>
                          {item.subtitle ? (
                            <span className="pulse-cmd__item-sub">{item.subtitle}</span>
                          ) : null}
                        </span>
                        <span className="pulse-cmd__item-go" aria-hidden="true">
                          →
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        <footer className="pulse-cmd__foot">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> navegar
          </span>
          <span>
            <kbd>Enter</kbd> abrir
          </span>
          <span>
            <kbd>Esc</kbd> cerrar
          </span>
        </footer>
      </div>
    </div>,
    document.body
  );
}
